import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Descriptions, Button, Input, Table, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import {
  formatDate,
  formatVND,
  mapStatusToVN,
  mapTechnicalConditionToVN,
  mapUnitToVN,
  isFirstAudit,
  getPreviousAudit,
  hasFieldChanged,
  parseSnapshot,
  getLocationName,
  type AuditRecord,
  type AccessoryItem,
  type Location,
  type Company,
  type Category,
  type Supplier,
  type Employee
} from '../../utils/auditHelpers';

interface AuditDetailModalProps {
  visible: boolean;
  onClose: () => void;
  auditDetail: AuditRecord | null;
  auditHistory: AuditRecord[];
  locations: Location[];
  companies: Company[];
  categories: Category[];
  suppliers: Supplier[];
  employees: Employee[];
  onSaveNotes: (notes: string) => Promise<void>;
  onApproveAudit?: (auditId: number, auditDocumentCode: string) => Promise<void>;
  showApproveButton?: boolean;
  title?: string;
}

const AuditDetailModal: React.FC<AuditDetailModalProps> = ({
  visible,
  onClose,
  auditDetail,
  auditHistory,
  locations,
  companies,
  categories,
  suppliers,
  employees,
  onSaveNotes,
  onApproveAudit,
  showApproveButton = false,
  title
}) => {
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editingNotes, setEditingNotes] = useState(auditDetail?.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [auditDocumentCode, setAuditDocumentCode] = useState('');
  const [approvingLoading, setApprovingLoading] = useState(false);

  useEffect(() => {
    setEditingNotes(auditDetail?.notes || '');
  }, [auditDetail]);

  // Helper functions
  const isFirstAuditLocal = useCallback((auditId: number): boolean => {
    return isFirstAudit(auditId, auditHistory);
  }, [auditHistory]);

  const getPreviousAuditLocal = useCallback((currentId: number): AuditRecord | undefined => {
    return getPreviousAudit(currentId, auditHistory);
  }, [auditHistory]);

  const hasFieldChangedLocal = useCallback((fieldName: string): boolean => {
    if (!auditDetail) return false;
    
    const previousAudit = getPreviousAuditLocal(auditDetail.id);
    if (!previousAudit || !previousAudit.snapshot) return false;
    
    const previousSnapshot = parseSnapshot(previousAudit.snapshot);
    const currentValue = parseSnapshot(auditDetail.snapshot)[fieldName];
    const previousValue = previousSnapshot[fieldName];
    
    return currentValue !== previousValue;
  }, [auditDetail, getPreviousAuditLocal]);

  // Helper functions để map ID thành tên
  const getCompanyName = useCallback((companyId: number | undefined | null): string => {
    if (!companyId) return '—';
    return companies.find(c => c.id === companyId)?.name || '—';
  }, [companies]);

  const getCategoryName = useCallback((categoryId: number | undefined | null): string => {
    if (!categoryId) return '—';
    return categories.find(c => c.id === categoryId)?.name || '—';
  }, [categories]);

  const getSupplierName = useCallback((supplierId: number | undefined | null): string => {
    if (!supplierId) return '—';
    return suppliers.find(s => s.id === supplierId)?.name || '—';
  }, [suppliers]);

  const getEmployeeName = useCallback((employeeId: number | undefined | null): string => {
    if (!employeeId) return '—';
    return employees.find(e => e.id === employeeId)?.full_name || String(employeeId);
  }, [employees]);

  const getLocationNameLocal = useCallback((locationId: number | undefined | null): string => {
    return getLocationName(locationId, locations);
  }, [locations]);

  const handleSaveNotes = async () => {
    if (!auditDetail) return;
    setSavingNotes(true);
    try {
      await onSaveNotes(editingNotes);
      setIsEditingNotes(false);
      message.success('Lưu ghi chú thành công');
    } catch (error) {
      message.error('Lỗi khi lưu ghi chú');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleApproveAudit = async () => {
    if (!auditDetail || !onApproveAudit || !auditDocumentCode.trim()) {
      message.error('Vui lòng nhập mã hồ sơ kiểm kê');
      return;
    }
    
    setApprovingLoading(true);
    try {
      await onApproveAudit(auditDetail.id, auditDocumentCode.trim());
      setShowApproveModal(false);
      setAuditDocumentCode('');
      message.success('Duyệt kiểm kê thành công');
    } catch (error) {
      message.error('Lỗi khi duyệt kiểm kê');
    } finally {
      setApprovingLoading(false);
    }
  };

  if (!auditDetail) return null;

  // Parse snapshot
  let snap: any = auditDetail.snapshot;
  if (snap && typeof snap === 'string') {
    try { snap = JSON.parse(snap); } catch { snap = {}; }
  }
  if (!snap || typeof snap !== 'object') snap = {};

  // Render value với icon "Mới" hoặc "Thay đổi" cho thông tin tài sản
  const renderAssetValueWithNewIcon = (value: string, fieldName: string): React.ReactElement => {
    const isNew = isFirstAuditLocal(auditDetail.id);
    const hasChanged = !isNew && hasFieldChangedLocal(fieldName);
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ 
          fontWeight: hasChanged ? '600' : '500',
          color: hasChanged ? '#fa8c16' : '#262626'
        }}>
          {value}
        </span>
        {isNew && (
          <span style={{ 
            fontSize: '11px', 
            color: '#52c41a',
            backgroundColor: '#f6ffed',
            padding: '2px 6px',
            borderRadius: '4px',
            border: '1px solid #b7eb8f',
            fontWeight: '500'
          }}>
            ✨ Mới
          </span>
        )}
        {hasChanged && (
          <span style={{ 
            fontSize: '11px', 
            color: '#fa8c16',
            backgroundColor: '#fff7e6',
            padding: '2px 6px',
            borderRadius: '4px',
            border: '1px solid #ffd591',
            fontWeight: '500'
          }}>
            🔄 Thay đổi
          </span>
        )}
      </div>
    );
  };

  // Helper function để hiển thị ngày kiểm kê gần nhất
  const getLastAuditDateDisplay = (snap: any, selectedAuditDetail: any): string => {
    // Lấy last_audit_date từ snapshot (đây là ngày tạo/chỉnh sửa tài sản tại thời điểm tạo snapshot)
    const lastAuditDate = snap.last_audit_date;
    
    // Nếu không có last_audit_date
    if (!lastAuditDate) {
      return 'Chưa có kiểm kê trước';
    }
    
    return formatDate(lastAuditDate);
  };

  const renderAccessoriesTable = () => {
    let currentAccessories = auditDetail.accessories_snapshot || [];
    
    // Parse accessories_snapshot nếu nó là string
    if (typeof currentAccessories === 'string') {
      try {
        currentAccessories = JSON.parse(currentAccessories);
      } catch (e) {
        currentAccessories = [];
      }
    }
    
    if (!currentAccessories.length) return null;

    const isNew = isFirstAuditLocal(auditDetail.id);
    const previousAudit = getPreviousAuditLocal(auditDetail.id);
    let previousAccessories = previousAudit?.accessories_snapshot || [];
    
    // Parse accessories_snapshot nếu nó là string
    if (typeof previousAccessories === 'string') {
      try {
        previousAccessories = JSON.parse(previousAccessories);
      } catch (e) {
        previousAccessories = [];
      }
    }

    // Helper function để kiểm tra accessory có mới không
    const isAccessoryNew = (accessory: any) => {
      if (isNew) return true; // Nếu là audit đầu tiên, tất cả đều mới
      
      // Tìm accessory cùng tên trong audit trước
      const previousAccessory = previousAccessories.find((prev: any) => 
        prev.name === accessory.name
      );
      
      return !previousAccessory; // Không tìm thấy trong danh sách trước = mới
    };

    // Helper function để kiểm tra accessory có thay đổi không
    const isAccessoryChanged = (accessory: any) => {
      if (isNew) return false; // Nếu là audit đầu tiên, không có thay đổi
      
      // Tìm accessory cùng tên trong audit trước
      const previousAccessory = previousAccessories.find((prev: any) => 
        prev.name === accessory.name
      );
      
      if (!previousAccessory) return false; // Không có trong audit trước = mới, không phải thay đổi
      
      // So sánh các thuộc tính
      return (
        previousAccessory.quantity !== accessory.quantity ||
        previousAccessory.unit !== accessory.unit ||
        previousAccessory.price !== accessory.price
      );
    };

    const accessoriesColumns = [
      {
        title: 'Tên vật tư',
        dataIndex: 'name',
        key: 'name',
        render: (name: string, record: any) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ 
              fontWeight: '500',
              color: '#262626'
            }}>
              {name}
            </span>
            {isAccessoryNew(record) && (
              <span style={{ 
                fontSize: '11px', 
                color: '#52c41a',
                backgroundColor: '#f6ffed',
                padding: '2px 6px',
                borderRadius: '4px',
                border: '1px solid #b7eb8f',
                fontWeight: '500'
              }}>
                ✨ Mới
              </span>
            )}
            {isAccessoryChanged(record) && (
              <span style={{ 
                fontSize: '11px', 
                color: '#fa8c16',
                backgroundColor: '#fff7e6',
                padding: '2px 6px',
                borderRadius: '4px',
                border: '1px solid #ffd591',
                fontWeight: '500'
              }}>
                🔄 Thay đổi
              </span>
            )}
          </div>
        ),
      },
      {
        title: 'Số lượng',
        dataIndex: 'quantity',
        key: 'quantity',
        render: (quantity: number, record: any) => (
          <span style={{ 
            fontWeight: isAccessoryChanged(record) ? '600' : '500',
            color: isAccessoryChanged(record) ? '#fa8c16' : '#262626'
          }}>
            {quantity}
          </span>
        ),
      },
      {
        title: 'Đơn vị',
        dataIndex: 'unit',
        key: 'unit',
        render: (unit: string, record: any) => (
          <span style={{ 
            fontWeight: isAccessoryChanged(record) ? '600' : '500',
            color: isAccessoryChanged(record) ? '#fa8c16' : '#262626'
          }}>
            {unit || '—'}
          </span>
        ),
      },
      {
        title: 'Giá',
        dataIndex: 'price',
        key: 'price',
        render: (price: number, record: any) => (
          <span style={{ 
            fontWeight: isAccessoryChanged(record) ? '600' : '500',
            color: isAccessoryChanged(record) ? '#fa8c16' : '#262626'
          }}>
            {price ? formatVND(price) : '—'}
          </span>
        ),
      },
      {
        title: 'Tổng giá',
        dataIndex: 'total_price',
        key: 'total_price',
        render: (totalPrice: number, record: any) => (
          <span style={{ 
            fontWeight: isAccessoryChanged(record) ? '600' : '500',
            color: isAccessoryChanged(record) ? '#fa8c16' : '#262626'
          }}>
            {totalPrice ? formatVND(totalPrice) : '—'}
          </span>
        ),
      },
    ];

    return (
      <div style={{
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        border: '1px solid #f0f0f0',
        padding: '16px'
      }}>
        <Table
          dataSource={currentAccessories}
          columns={accessoriesColumns}
          rowKey="name"
          size="small"
          pagination={false}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '6px',
            overflow: 'hidden'
          }}
        />
      </div>
    );
  };

  // Technical Specifications Display
  const TechnicalSpecificationsDisplay: React.FC<{ specs: any }> = ({ specs }) => {
    if (!specs || typeof specs !== 'object') return null;

    const specItems = Object.entries(specs).filter(([_, value]) => value !== null && value !== undefined && value !== '');
    const isNew = isFirstAuditLocal(auditDetail.id);

    // Kiểm tra thay đổi cho thông số kỹ thuật
    const hasSpecChanged = (specKey: string, currentValue: any): boolean => {
      if (isNew) return false;
      const previousAudit = getPreviousAuditLocal(auditDetail.id);
      if (!previousAudit || !previousAudit.snapshot) return false;
      
      const previousSnapshot = parseSnapshot(previousAudit.snapshot);
      const previousSpecs = previousSnapshot.technical_specifications;
      if (!previousSpecs) return false;
      
      return previousSpecs[specKey] !== currentValue;
    };

    return (
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '12px',
        padding: '16px',
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        border: '1px solid #f0f0f0'
      }}>
        {specItems.map(([key, value], index) => {
          const hasChanged = hasSpecChanged(key, value);
          
          return (
            <div key={key} style={{ 
              padding: '12px 16px', 
              backgroundColor: hasChanged ? '#fff7e6' : '#ffffff', 
              borderRadius: '6px',
              border: `1px solid ${hasChanged ? '#ffd591' : '#d9d9d9'}`,
              boxShadow: hasChanged ? '0 2px 4px rgba(250, 140, 22, 0.1)' : '0 1px 2px rgba(0, 0, 0, 0.03)',
              transition: 'all 0.2s ease'
            }}>
              <div style={{ 
                fontWeight: '600', 
                fontSize: '13px', 
                color: '#262626', 
                marginBottom: '6px',
                lineHeight: '1.4'
              }}>
                {key}
              </div>
              <div style={{ 
                fontSize: '14px', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                minHeight: '20px'
              }}>
                <span style={{ 
                  fontWeight: hasChanged ? '600' : '400',
                  color: hasChanged ? '#fa8c16' : '#595959',
                  flex: 1
                }}>
                  {String(value) || '—'}
                </span>
                {isNew && (
                  <span style={{ 
                    fontSize: '11px', 
                    color: '#52c41a',
                    backgroundColor: '#f6ffed',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    border: '1px solid #b7eb8f',
                    fontWeight: '500'
                  }}>
                    ✨ Mới
                  </span>
                )}
                {hasChanged && (
                  <span style={{ 
                    fontSize: '11px', 
                    color: '#fa8c16',
                    backgroundColor: '#fff7e6',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    border: '1px solid #ffd591',
                    fontWeight: '500'
                  }}>
                    🔄 Thay đổi
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Modal
        title={title || "Chi tiết kiểm kê"}
        open={visible}
        onCancel={onClose}
        footer={null}
        width={800}
        destroyOnClose
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Thông tin kiểm kê */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px', color: '#262626' }}>Thông tin kiểm kê</h3>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Ngày kiểm kê">
                {formatDate(auditDetail.audit_date)}
              </Descriptions.Item>
              <Descriptions.Item label="Kết quả">
                {auditDetail.result || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Tình trạng">
                {auditDetail.status || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Loại kiểm kê">
                {auditDetail.type || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Hồ sơ kiểm kê">
                {(() => {
                  const isNew = isFirstAuditLocal(auditDetail.id);
                  const previousAudit = getPreviousAuditLocal(auditDetail.id);
                  const hasChanged = !isNew && previousAudit && 
                    previousAudit.audit_document_code !== auditDetail.audit_document_code;
                  
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ 
                        fontWeight: hasChanged ? '600' : '500',
                        color: hasChanged ? '#fa8c16' : '#262626'
                      }}>
                        {auditDetail.audit_document_code || '—'}
                      </span>
                      {isNew && (
                        <span style={{ 
                          fontSize: '11px', 
                          color: '#52c41a',
                          backgroundColor: '#f6ffed',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          border: '1px solid #b7eb8f',
                          fontWeight: '500'
                        }}>
                          ✨ Mới
                        </span>
                      )}
                      {hasChanged && (
                        <span style={{ 
                          fontSize: '11px', 
                          color: '#fa8c16',
                          backgroundColor: '#fff7e6',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          border: '1px solid #ffd591',
                          fontWeight: '500'
                        }}>
                          🔄 Thay đổi
                        </span>
                      )}
                    </div>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Ghi chú" span={2}>
                {isEditingNotes ? (
                  <div>
                    <Input.TextArea
                      value={editingNotes}
                      onChange={(e) => setEditingNotes(e.target.value)}
                      rows={3}
                      style={{ marginBottom: '8px' }}
                    />
                    <div>
                      <Button 
                        type="primary" 
                        size="small" 
                        onClick={handleSaveNotes}
                        loading={savingNotes}
                        style={{ marginRight: '8px' }}
                      >
                        Lưu
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => {
                          setIsEditingNotes(false);
                          setEditingNotes(auditDetail.notes || '');
                        }}
                      >
                        Hủy
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <span>{auditDetail.notes || '—'}</span>
                    <Button 
                      type="link" 
                      size="small" 
                      onClick={() => setIsEditingNotes(true)}
                    >
                      Chỉnh sửa
                    </Button>
                  </div>
                )}
              </Descriptions.Item>
            </Descriptions>
          </div>

          {/* Thông tin tài sản */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px', color: '#262626' }}>Thông tin tài sản</h3>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Mã tài sản">
                {renderAssetValueWithNewIcon(snap.asset_code || '—', 'asset_code')}
              </Descriptions.Item>
              <Descriptions.Item label="Tên tài sản">
                {renderAssetValueWithNewIcon(snap.name || '—', 'name')}
              </Descriptions.Item>
              <Descriptions.Item label="Danh mục">
                {renderAssetValueWithNewIcon(getCategoryName(snap.category_id), 'category_id')}
              </Descriptions.Item>
              <Descriptions.Item label="Công ty">
                {renderAssetValueWithNewIcon(getCompanyName(snap.company_id), 'company_id')}
              </Descriptions.Item>
              <Descriptions.Item label="Nhà cung cấp">
                {renderAssetValueWithNewIcon(getSupplierName(snap.supplier_id), 'supplier_id')}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày mua">
                {renderAssetValueWithNewIcon(formatDate(snap.purchase_date), 'purchase_date')}
              </Descriptions.Item>
              <Descriptions.Item label="Giá mua">
                {renderAssetValueWithNewIcon(snap.purchase_cost ? formatVND(snap.purchase_cost) : '—', 'purchase_cost')}
              </Descriptions.Item>
              <Descriptions.Item label="Bảo hành (tháng)">
                {renderAssetValueWithNewIcon(snap.warranty_period_months ? String(snap.warranty_period_months) : '—', 'warranty_period_months')}
              </Descriptions.Item>
              <Descriptions.Item label="Nhân viên sử dụng">
                {renderAssetValueWithNewIcon(getEmployeeName(snap.assigned_to_employee_id), 'assigned_to_employee_id')}
              </Descriptions.Item>
              <Descriptions.Item label="Tình trạng kỹ thuật">
                {renderAssetValueWithNewIcon(snap.technical_condition || auditDetail.technical_condition || '—', 'technical_condition')}
              </Descriptions.Item>
              <Descriptions.Item label="Tình trạng tài sản">
                {renderAssetValueWithNewIcon(snap.status || '—', 'status')}
              </Descriptions.Item>
              <Descriptions.Item label="Vị trí">
                {renderAssetValueWithNewIcon(getLocationNameLocal(snap.location_id), 'location_id')}
              </Descriptions.Item>
            </Descriptions>
            
            {/* Thông số kỹ thuật */}
            {snap.technical_specifications && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ 
                  fontWeight: '500', 
                  color: '#262626',
                  marginBottom: '8px',
                  fontSize: '13px'
                }}>
                  Thông số kỹ thuật:
                </div>
                <TechnicalSpecificationsDisplay specs={snap.technical_specifications} />
              </div>
            )}
            
            {/* Vật tư / Thiết bị */}
            {auditDetail.accessories_snapshot && auditDetail.accessories_snapshot.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ 
                  fontWeight: '500', 
                  color: '#262626',
                  marginBottom: '8px',
                  fontSize: '13px'
                }}>
                  Vật tư / Thiết bị:
                </div>
                {renderAccessoriesTable()}
              </div>
            )}
          </div>

          {/* Action buttons */}
          {showApproveButton && auditDetail.status === 'pending' && auditDetail.type !== 'transfer' && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <Button 
                type="primary" 
                onClick={() => setShowApproveModal(true)}
                icon={<EyeOutlined />}
              >
                Duyệt kiểm kê
              </Button>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal nhập mã hồ sơ kiểm kê */}
      {showApproveButton && auditDetail?.type !== 'transfer' && (
        <Modal
          title="Nhập mã hồ sơ kiểm kê"
          open={showApproveModal}
          onCancel={() => {
            setShowApproveModal(false);
            setAuditDocumentCode('');
          }}
          onOk={handleApproveAudit}
          confirmLoading={approvingLoading}
          okText="Duyệt"
          cancelText="Hủy"
          okButtonProps={{
            disabled: !auditDocumentCode.trim()
          }}
        >
          <div style={{ padding: '16px 0' }}>
            <p style={{ marginBottom: '16px', color: '#666' }}>
              Vui lòng nhập mã yêu cầu từ portal office để duyệt.
            </p>
            <Input
              placeholder="Nhập mã hồ sơ kiểm kê..."
              value={auditDocumentCode}
              onChange={(e) => setAuditDocumentCode(e.target.value)}
              onPressEnter={handleApproveAudit}
              autoFocus
            />
          </div>
        </Modal>
      )}
    </>
  );
};

export default AuditDetailModal; 